import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  NewspaperIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { News, TeamActivity } from '../types';
import Carousel from '../components/Carousel';
import { motion } from 'framer-motion';

const HOME_CACHE_KEY = 'home:v1';
const ACTIVITIES_CACHE_KEY = 'home:activities:v1';
const CACHE_TTL_MS = 60 * 1000;

type HomePayload = { latestNews: News[] };

function readCache<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: T };
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, data: T) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore quota / private mode
  }
}

const highlights = [
  {
    title: '高效钙钛矿太阳能电池',
    description: '单结钙钛矿太阳能电池光电转换效率超过26%',
    image: '/highlights/highlight-solar-cell.jpg'
  },
  {
    title: '国际一流研究团队',
    description: '拥有多位知名学者和优秀研究生',
    image: '/highlights/highlight-research-team.jpg'
  },
  {
    title: '前沿科研成果',
    description: '国际上最早从事钙钛矿太阳能电池研究的团队之一',
    image: '/highlights/highlight-research-achievements.jpg'
  }
];

const Home: React.FC = () => {
  const [latestNews, setLatestNews] = useState<News[]>([]);
  const [teamActivities, setTeamActivities] = useState<TeamActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const activitiesSectionRef = useRef<HTMLElement | null>(null);
  const activitiesFetchedRef = useRef(false);

  // 首屏：只拉首页聚合接口
  useEffect(() => {
    const cached = readCache<HomePayload>(HOME_CACHE_KEY);
    if (cached?.latestNews) {
      setLatestNews(cached.latestNews);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchHome = async () => {
      try {
        const response = await apiService.get<HomePayload>('/home');
        if (cancelled) return;
        if (response.success && response.data?.latestNews) {
          setLatestNews(response.data.latestNews);
          writeCache(HOME_CACHE_KEY, { latestNews: response.data.latestNews });
        }
      } catch (error) {
        console.error('获取首页数据失败:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHome();
    return () => {
      cancelled = true;
    };
  }, []);

  // 团队活动：进入视口后再请求
  useEffect(() => {
    const cached = readCache<TeamActivity[]>(ACTIVITIES_CACHE_KEY);
    if (cached) {
      setTeamActivities(cached);
      activitiesFetchedRef.current = true;
      return;
    }

    const el = activitiesSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // 无 Observer 时退化为直接加载
      void loadActivities();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          void loadActivities();
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const loadActivities = async () => {
    if (activitiesFetchedRef.current) return;
    activitiesFetchedRef.current = true;
    setActivitiesLoading(true);
    try {
      const response = await apiService.get<TeamActivity[]>('/team-activities');
      if (response.success && response.data) {
        setTeamActivities(response.data);
        writeCache(ACTIVITIES_CACHE_KEY, response.data);
      }
    } catch (error) {
      console.error('获取团队活动失败:', error);
      activitiesFetchedRef.current = false;
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div>
      {/* 英雄区域 - 全屏背景图 */}
      <section className="relative text-white overflow-hidden min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="/background.jpg"
            alt="课题组实验室背景"
            className="w-full h-full object-cover filter blur-none scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              张文华课题组
            </h1>
            <p className="text-lg sm:text-xl mb-10 max-w-3xl mx-auto">
              专注于钙钛矿材料与器件的前沿研究，致力于推动新能源技术的发展，
              为可持续发展贡献力量。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/research"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
              >
                了解研究方向
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
              >
                联系我们
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 研究亮点 */}
      <motion.section
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">研究亮点</h2>
            <p className="text-lg text-gray-600">
              我们在钙钛矿材料与器件研究方面取得的重要成果
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((highlight) => (
              <div key={highlight.title} className="card text-center">
                <div className="mx-auto mb-6 w-28 h-28 overflow-hidden rounded-xl bg-gray-100 shadow-sm">
                  <img
                    src={highlight.image}
                    alt={highlight.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{highlight.title}</h3>
                <p className="text-gray-600">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* 最新动态 */}
      <motion.section
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">最新动态</h2>
            <Link
              to="/news"
              className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center"
            >
              查看全部
              <ArrowRightIcon className="ml-1 h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : latestNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestNews.map((news) => (
                <Link key={news._id} to={`/news/${news._id}`} className="card hover:shadow-lg transition-shadow">
                  {news.coverImage && (
                    <div className="mb-4">
                      <img
                        src={news.coverImage}
                        alt={news.title}
                        className="w-full h-48 object-cover rounded-lg"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">
                      {news.category}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(news.publishDate || news.createdAt)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {news.title}
                  </h3>
                  {news.summary && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {news.summary}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <NewspaperIcon className="h-4 w-4 mr-1" />
                      新闻动态
                    </div>
                    <span>浏览 {news.viewCount ?? 0}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <NewspaperIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">暂无最新动态</p>
            </div>
          )}
        </div>
      </motion.section>

      {/* 课题组介绍 */}
      <motion.section
        className="py-16"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">关于我们</h2>
              <p className="text-lg text-gray-600 mb-6">
                云南大学张文华课题组成立于2021年，专注于钙钛矿材料与器件的前沿研究。
                我们致力于解决钙钛矿太阳能电池器件在效率、稳定性和成本方面的关键科学问题。
              </p>
              <p className="text-lg text-gray-600 mb-8">
                课题组拥有完善的实验设备和研究条件，与国内外多家知名高校和研究机构
                建立了良好的合作关系，为学生提供广阔的学术发展平台。
              </p>
              <Link
                to="/members"
                className="btn-primary inline-flex items-center"
              >
                了解团队成员
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg p-8">
              <div className="text-center">
                <AcademicCapIcon className="h-16 w-16 text-primary-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">研究实力</h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">60+</div>
                    <div className="text-sm text-gray-600">团队成员</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600">50+</div>
                    <div className="text-sm text-gray-600">发表论文</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600">20+</div>
                    <div className="text-sm text-gray-600">研究项目</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600">26%+</div>
                    <div className="text-sm text-gray-600">电池效率</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* 团队活动（视口内再请求） */}
      <motion.section
        ref={activitiesSectionRef}
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">团队活动</h2>
            <p className="text-lg text-gray-600">
              记录课题组的重要时刻和精彩瞬间
            </p>
          </div>
          {activitiesLoading && teamActivities.length === 0 ? (
            <div className="h-96 bg-gray-100 rounded-lg animate-pulse" />
          ) : (
            <Carousel
              items={teamActivities}
              autoPlay={true}
              interval={6000}
              showIndicators={true}
              showArrows={true}
              height="h-96"
            />
          )}
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
